/**
 * Manual Payment Component
 * Form for creating manual payments for certificate applications
 */

import React, { useState } from "react";
import { paymentService } from "../services/api/payment.service";
import type { CreatePaymentDto } from "../types/payment.types";
import type { CertificateApplication } from "../types/certificate.types";
import { UserStatusId } from "../types/user.types";
import BusyOverlay from "../components/common/BusyOverlay";
import { showSuccessToast, showErrorToast, showConfirm } from "../utils/toast";
import {
  GENERIC_ERROR_MESSAGE,
  REQUIRED_FIELD_MESSAGE,
} from "../utils/constants";
import "../styles/pages/Users.css";

interface ManualPaymentProps {
  application: CertificateApplication;
  onClose: () => void;
  onPaymentSuccess?: () => void;
}

export const ManualPayment: React.FC<ManualPaymentProps> = ({
  application,
  onClose,
  onPaymentSuccess,
}) => {
  const [error, setError] = useState("");

  const [formData, setFormData] = useState<CreatePaymentDto>({
    certificateApplicationId: application.id || 0,
    lraPayment: application.lraPayment ?? application.serviceCharge ?? 0,
    operationalCost: application.operationalCost ?? 0,
    currencyId: application.currencyId || 0,
    statusId: UserStatusId.Created,
    paymentMode: "Manual",
    paymentReferenceNumber: "",
    revenueNumber: "",
    documentPath: [],
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    console.log("=== FILE CHANGE EVENT ===");
    console.log("Files selected:", files);
    if (files) {
      const fileArray = Array.from(files);
      console.log("File array:", fileArray);
      console.log(
        "File details:",
        fileArray.map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type,
        })),
      );
      setFormData({ ...formData, documentPath: fileArray });
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.documentPath;
        return newErrors;
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmitPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    const errors: Record<string, string> = {};

    console.log("=== MANUAL PAYMENT SUBMIT ===");
    console.log("Form Data:", formData);
    console.log(
      "Certificate Application ID:",
      formData.certificateApplicationId,
    );
    console.log("LRA Payment:", formData.lraPayment);
    console.log("Operational Cost:", formData.operationalCost);
    console.log("Currency ID:", formData.currencyId);
    console.log("Status ID:", formData.statusId);
    console.log("Payment Mode:", formData.paymentMode);
    console.log("Payment Reference Number:", formData.paymentReferenceNumber);
    console.log("Revenue Number:", formData.revenueNumber);
    console.log("Document Path (files):", formData.documentPath);

    // Validation
    if (formData.certificateApplicationId <= 0) {
      setFormError("Invalid certificate application");
      return;
    }
    if (formData.lraPayment <= 0) {
      setFormError("LRA Payment must be greater than 0");
      return;
    }
    if (formData.operationalCost < 0) {
      setFormError("Operational Cost cannot be negative");
      return;
    }
    if (
      !formData.paymentReferenceNumber ||
      formData.paymentReferenceNumber.trim() === ""
    ) {
      errors.paymentReferenceNumber = REQUIRED_FIELD_MESSAGE;
    }
    if (!formData.revenueNumber || formData.revenueNumber.trim() === "") {
      errors.revenueNumber = REQUIRED_FIELD_MESSAGE;
    }
    if (!formData.documentPath || formData.documentPath.length === 0) {
      errors.documentPath = REQUIRED_FIELD_MESSAGE;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    // Show confirmation dialog
    const confirmed = await showConfirm(
      "Confirm Manual Payment",
      "Are you sure you want to submit this manual payment? Please ensure all details are correct.",
      "Yes, submit payment",
      "Cancel",
    );
    if (!confirmed) return;

    try {
      setFormLoading(true);
      console.log("Calling paymentService.createPayment...");
      await paymentService.createPayment(formData);
      console.log("Payment created successfully!");
      showSuccessToast("Payment created successfully");
      onPaymentSuccess?.();
      onClose();
    } catch (err) {
      console.error("Payment creation failed:", err);
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay--side-panel">
      <div className="modal-content modal-content--side-panel">
        <BusyOverlay isVisible={formLoading} message="Creating payment..." />

        <div className="modal-header">
          <h2 className="modal-title">Manual Payment</h2>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            disabled={formLoading}
          >
            ✕
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmitPayment}>
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError("")}
                className="alert-close"
              >
                ✕
              </button>
            </div>
          )}

          {formError && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{formError}</span>
              <button
                type="button"
                onClick={() => setFormError("")}
                className="alert-close"
              >
                ✕
              </button>
            </div>
          )}

          {/* Application Information Display */}
          <div
            style={{
              background: "#f8f9fa",
              padding: "1rem",
              borderRadius: "4px",
              marginBottom: "1.5rem",
              border: "1px solid #dee2e6",
            }}
          >
            <h3
              style={{
                fontSize: "0.875rem",
                fontWeight: "600",
                marginBottom: "0.75rem",
                color: "#495057",
              }}
            >
              Application Details
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "0.5rem",
                fontSize: "0.875rem",
              }}
            >
              <div>
                <span style={{ color: "#6c757d" }}>Form No:</span>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500" }}>
                  {application.formNo}
                </span>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Category:</span>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500" }}>
                  {application.category}
                </span>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>County:</span>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500" }}>
                  {application.countyName || "—"}
                </span>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Email:</span>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500" }}>
                  {application.email || "—"}
                </span>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>LRA Payment:</span>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500" }}>
                  {application.currencyName || ""} {formData.lraPayment}
                </span>
              </div>
              <div>
                <span style={{ color: "#6c757d" }}>Operational Cost:</span>
                <span style={{ marginLeft: "0.5rem", fontWeight: "500" }}>
                  {application.currencyName || ""} {formData.operationalCost}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Reference Number */}
          <div
            className={`form-group ${formData.paymentReferenceNumber ? "has-value" : ""} ${validationErrors.paymentReferenceNumber ? "form-group--error" : ""}`}
          >
            <input
              type="text"
              id="paymentReferenceNumber"
              name="paymentReferenceNumber"
              className={`form-input ${validationErrors.paymentReferenceNumber ? "form-input--error" : ""}`}
              value={formData.paymentReferenceNumber || ""}
              onChange={handleInputChange}
              placeholder=" "
              disabled={formLoading}
              required
            />
            <label className="form-label">
              Payment Reference Number <span className="required">*</span>
            </label>
            {validationErrors.paymentReferenceNumber && (
              <div className="field-error">
                {validationErrors.paymentReferenceNumber}
              </div>
            )}
          </div>

          {/* Revenue Number */}
          <div
            className={`form-group ${formData.revenueNumber ? "has-value" : ""} ${validationErrors.revenueNumber ? "form-group--error" : ""}`}
          >
            <input
              type="text"
              id="revenueNumber"
              name="revenueNumber"
              className={`form-input ${validationErrors.revenueNumber ? "form-input--error" : ""}`}
              value={formData.revenueNumber || ""}
              onChange={handleInputChange}
              placeholder=" "
              disabled={formLoading}
              required
            />
            <label className="form-label">
              Revenue Number <span className="required">*</span>
            </label>
            {validationErrors.revenueNumber && (
              <div className="field-error">
                {validationErrors.revenueNumber}
              </div>
            )}
          </div>

          {/* File Upload for Payment Receipt and Documents */}
          <div
            className={`form-group ${formData.documentPath && formData.documentPath.length > 0 ? "has-value" : ""} ${validationErrors.documentPath ? "form-group--error" : ""}`}
          >
            <input
              type="file"
              id="documentPath"
              name="documentPath"
              className={`form-input ${validationErrors.documentPath ? "form-input--error" : ""}`}
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              disabled={formLoading}
              style={{ padding: "0.5rem" }}
              placeholder=" "
              required
            />
            <label className="form-label">
              Payment Receipt & Documents <span className="required">*</span>
            </label>
            {validationErrors.documentPath && (
              <div className="field-error">{validationErrors.documentPath}</div>
            )}
            {formData.documentPath && formData.documentPath.length > 0 && (
              <div style={{ marginTop: "0.5rem", fontSize: "0.875rem" }}>
                <strong>Selected file:</strong>
                <div style={{ marginTop: "0.25rem" }}>
                  {formData.documentPath[0].name} (
                  {(formData.documentPath[0].size / 1024).toFixed(2)} KB)
                </div>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn--outline"
              onClick={onClose}
              disabled={formLoading}
            >
              Close
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={formLoading}
            >
              {formLoading ? (
                <>
                  <span className="spinner spinner--sm"></span>
                  Creating...
                </>
              ) : (
                "Create Payment"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualPayment;
